import { Callout } from '../tenants/parts';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import EmailRounded from '@mui/icons-material/EmailRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useAuth } from '../../auth/AuthProvider';
import { SendMessageDialog, WHATSAPP_GREEN } from '../messages/SendMessageDialog';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Segmented } from '../../components/ui';
import { date, plural } from '../../lib/format';
import { errorMessage } from '../../lib/notify';
import { fetchPlans, tenantKeys } from '../tenants/api';
import { CHANNELS, type CodeChannel, type CreateCodeBody, type CreatedCode } from './api';

const intIn = (v: string, min: number, max: number) => {
  if (!/^\d+$/.test(v.trim())) return null;
  const n = Number(v);
  return n >= min && n <= max ? n : null;
};

/**
 * New activation code for one shop: a duration in days or a plan (exactly one, CreateCodeSchema),
 * the code's own expiry (1–365 days, default 30) and optional notes.
 */
export function CreateCodeDialog({
  open,
  tenantName,
  onCreate,
  onClose,
}: {
  open: boolean;
  tenantName: string;
  onCreate: (body: CreateCodeBody) => Promise<unknown>;
  onClose: () => void;
}) {
  const plans = useQuery({ queryKey: tenantKeys.plans, queryFn: fetchPlans, enabled: open });
  const activePlans = (plans.data?.data ?? []).filter((p) => p.isActive);
  const [now] = useState(() => Date.now());
  const [mode, setMode] = useState<'days' | 'plan'>('days');
  const [days, setDays] = useState('30');
  const [planId, setPlanId] = useState('');
  const [expires, setExpires] = useState('30');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const daysN = intIn(days, 1, 3660);
  const expiresN = intIn(expires, 1, 365);
  const plan = activePlans.find((p) => p.id === planId);
  const valid = (mode === 'days' ? daysN !== null : Boolean(plan)) && expiresN !== null;

  const reset = () => {
    setMode('days');
    setDays('30');
    setPlanId('');
    setExpires('30');
    setNotes('');
    setSubmitted(false);
    setError(null);
  };
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const submit = () => {
    setSubmitted(true);
    if (!valid) return;
    const body: CreateCodeBody = {
      expiresInDays: expiresN,
      ...(mode === 'days' ? { durationDays: daysN ?? 1 } : { planId }),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    setBusy(true);
    setError(null);
    onCreate(body)
      .then(() => {
        setBusy(false);
        reset();
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };

  const grants = mode === 'days' ? daysN : (plan?.durationDays ?? null);

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>New activation code</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25}>
            <Typography color="text.secondary" variant="body2">
              For <strong>{tenantName}</strong>. Only this shop can redeem it.
            </Typography>
            {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
            <Segmented
              label="Duration from"
              value={mode}
              options={[
                { value: 'days', label: 'Number of days' },
                { value: 'plan', label: 'A plan' },
              ]}
              onChange={setMode}
            />
            {mode === 'days' ? (
              <TextField
                label="Days added when redeemed"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                error={submitted && daysN === null}
                helperText={submitted && daysN === null ? 'Enter 1–3660 days.' : ' '}
                required
              />
            ) : (
              <TextField
                select
                label="Plan"
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                error={submitted && !plan}
                helperText={
                  activePlans.length === 0 && !plans.isPending
                    ? 'No active plans. Create one under Plans, or use a number of days.'
                    : submitted && !plan
                      ? 'Choose a plan.'
                      : ' '
                }
                required
              >
                {activePlans.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} · {plural(p.durationDays, 'day')}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Code expires after (days)"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              error={submitted && expiresN === null}
              helperText={
                submitted && expiresN === null
                  ? 'Enter 1–365 days.'
                  : expiresN === null
                    ? ' '
                    : `Unused codes stop working on ${date(new Date(now + expiresN * 86_400_000).toISOString())}.`
              }
              required
            />
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
            {grants !== null && (
              <Callout severity="info" icon={<KeyRounded fontSize="small" />}>
                Redeeming this code adds {plural(grants, 'day')} to the shop’s paid period.
              </Callout>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={busy}>
            Generate code
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * Shows a newly generated code exactly once. It cannot be dismissed by Esc or the backdrop, and
 * "Done" waits until the admin has copied the code or confirmed they saved it.
 */
export function CodeRevealDialog({
  code,
  tenantName,
  onClose,
}: {
  code: CreatedCode | null;
  tenantName: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [sending, setSending] = useState<'WHATSAPP' | 'EMAIL' | null>(null);
  const [sentBy, setSentBy] = useState<string | null>(null);
  const { can } = useAuth();

  const done = () => {
    setCopied(false);
    setSaved(false);
    setCopyFailed(false);
    setSentBy(null);
    onClose();
  };

  const copy = () => {
    if (!code) return;
    const clip = (navigator as Partial<Navigator>).clipboard;
    if (!clip) {
      setCopyFailed(true);
      return;
    }
    clip.writeText(code.code).then(
      () => {
        setCopied(true);
        setCopyFailed(false);
      },
      () => setCopyFailed(true),
    );
  };

  return (
    <Dialog open={code !== null} maxWidth="xs" fullWidth aria-labelledby="code-reveal-title">
      <DialogTitle id="code-reveal-title" sx={{ fontWeight: 700 }}>
        Activation code created
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Callout severity="warning">
            <strong>Copy this code now.</strong> It is shown only once and can’t be retrieved later.
            If it is lost, revoke it and generate a new one.
          </Callout>
          <Box
            sx={(t) => {
              const p = t.vars?.palette.primary.main ?? t.palette.primary.main;
              return {
                p: 2,
                borderRadius: '14px',
                textAlign: 'center',
                border: `1px dashed color-mix(in srgb, ${p} 45%, transparent)`,
                bgcolor: `color-mix(in srgb, ${p} 6%, transparent)`,
              };
            }}
          >
            <Typography
              data-testid="plain-code"
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontSize: { xs: '1.15rem', sm: '1.4rem' },
                fontWeight: 700,
                letterSpacing: '0.08em',
                userSelect: 'all',
                wordBreak: 'break-all',
              }}
            >
              {code?.code}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {tenantName} · adds {plural(code?.durationDays ?? 0, 'day')} · expires{' '}
              {date(code?.expiresAt)}
            </Typography>
          </Box>
          <Button
            variant={copied ? 'outlined' : 'contained'}
            color={copied ? 'success' : 'primary'}
            startIcon={copied ? <CheckRounded /> : <ContentCopyRounded />}
            onClick={copy}
          >
            {copied ? 'Copied' : 'Copy code'}
          </Button>
          {copyFailed && (
            <Callout severity="info">
              Copying isn’t available here. Select the code and copy it.
            </Callout>
          )}
          {can('support.chat') && (
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<WhatsAppIcon sx={{ color: WHATSAPP_GREEN }} />}
                onClick={() => setSending('WHATSAPP')}
              >
                Send on WhatsApp
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EmailRounded />}
                onClick={() => setSending('EMAIL')}
              >
                Send by email
              </Button>
            </Stack>
          )}
          {sentBy && <Callout severity="success">Sent to {sentBy} and marked delivered.</Callout>}
          <FormControlLabel
            control={<Checkbox checked={saved} onChange={(e) => setSaved(e.target.checked)} />}
            label="I have saved or sent this code"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="contained" onClick={done} disabled={!copied && !saved && !sentBy}>
          Done
        </Button>
      </DialogActions>
      {sending && code && (
        <SendMessageDialog
          tenant={{ id: code.tenantId, name: tenantName }}
          channel={sending}
          extra={codeLines(code.code, code.durationDays, date(code.expiresAt))}
          codeId={code.id}
          onSent={(m) => {
            setSentBy(m.channel === 'WHATSAPP' ? `+${m.recipient}` : m.recipient);
            setSaved(true);
          }}
          onClose={() => setSending(null)}
        />
      )}
    </Dialog>
  );
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The code's lines added after the tenant-page template when it is sent to the shop. */
function codeLines(value: string, days: number, expires: string) {
  const redeem = `Redeem it in PackPilot → Subscription before ${expires}.`;
  return {
    text: `Your PackPilot activation code: ${value}\nIt adds ${String(days)} days. ${redeem}`,
    html:
      `<table><tbody><tr><th>Activation code</th><td><strong>${esc(value)}</strong></td></tr>` +
      `<tr><th>Adds</th><td>${String(days)} days</td></tr>` +
      `<tr><th>Redeem before</th><td>${esc(expires)}</td></tr></tbody></table>` +
      `<p>${esc(redeem)}</p>`,
  };
}

/** Records how the code reached the shop. */
export function DeliverDialog({
  open,
  hint,
  onDeliver,
  onClose,
}: {
  open: boolean;
  hint: string;
  onDeliver: (channel: CodeChannel) => Promise<unknown>;
  onClose: () => void;
}) {
  const [channel, setChannel] = useState<CodeChannel>('whatsapp');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const close = () => {
    if (busy) return;
    setError(null);
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Mark code as delivered</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25}>
          <Typography color="text.secondary" variant="body2">
            Record how the code ending <strong className="tabular">{hint}</strong> was sent to the
            shop.
          </Typography>
          {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
          <TextField
            select
            label="Channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value as CodeChannel)}
          >
            {CHANNELS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            onDeliver(channel)
              .then(() => {
                setBusy(false);
                onClose();
              })
              .catch((e: unknown) => {
                setBusy(false);
                setError(e);
              });
          }}
        >
          Mark delivered
        </Button>
      </DialogActions>
    </Dialog>
  );
}
